import { connect } from 'react-redux';
import React, { Fragment, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { GIButton, Modal } from 'app-components';

import { configs } from '@constants/configs/configs';
import { normalizeFileSize } from '@utils/normalizeFileSize.utils';

import UploadedFileList from '@components/individualPortal/ticket/UploadedFileList';
import NewFileList from '@components/individualPortal/ticket/NewFileList';

import {
    getAttachments,
    postAttachments,
    deleteAttachment,
} from '@actions/individualPortal/ticket/ticketActionCreators';
import { ACCEPTED_FILE_TYPES } from './TicketConstant';

const TicketFileContainer = props => {
    const {
        ticket,
        encTicketId,
        ticketFileData: { uploadSuccess, uploadError, attachments, deleteSuccess },
        actions: { getAttachments, postAttachments, deleteAttachment },
    } = props;

    const [files, setFiles] = useState<any[]>([]);
    const [isShowModal, setIsShowModal] = useState(false);
    const [isFileError, setIsFileError] = useState(false);
    const [docPathDeleteFile, setdocPathDeleteFile] = useState('');
    const [isShowAttachmentDeletModal, setIsShowAttachmentDeletModal] = useState(false);

    useEffect(() => {
        getAttachments(`${configs.endpoints.GET_ATTACHMENTS_URL}/${encTicketId}`);
    }, []);

    useEffect(() => {
        if (uploadSuccess || deleteSuccess) {
            getAttachments(`${configs.endpoints.GET_ATTACHMENTS_URL}/${encTicketId}`);
            if (uploadSuccess) {
                setFiles([]);
            }
        }
    }, [uploadSuccess, deleteSuccess]);

    useEffect(() => {
        if (uploadError) {
            setIsShowModal(true);
        }
    }, [uploadError]);

    const onFileSelect = () => {
        let input = document.createElement('input');
        input.type = 'file';
        input.onchange = () => {
            const newFile: FileList | null = input.files;
            const fileNameArray: string[] | [] = newFile?.[0]?.name?.split('.') || [];
            const fileNameLength: number = fileNameArray?.length || 0;
            const isValidFile = ACCEPTED_FILE_TYPES.includes(fileNameArray[fileNameLength - 1]?.toUpperCase());
            if (isValidFile) {
                setIsFileError(false);
                setFiles(files => [
                    ...files,
                    { name: newFile?.[0].name, size: normalizeFileSize(newFile?.[0].size, 1), file: newFile?.[0] },
                ]);
            } else {
                setIsFileError(true);
            }
        };
        input.click();
    };

    const onDeleteFile = index => {
        setFiles(files => files.filter((s, i) => i !== index));
    };

    const onOpenDeleteAttachmentPopup = docPath => {
        if (docPath) {
            setIsShowAttachmentDeletModal(true);
            setdocPathDeleteFile(docPath);
        }
    };

    const onFileUpload = () => {
        let formData = new FormData();
        formData.append('ticketId', ticket.id);

        files.map((file: any) => formData.append('myfiles', file.file));
        postAttachments(configs.endpoints.POST_ATTACHMENTS_URL, formData);
    };

    const onCancelUpload = () => {
        setFiles(files => files.filter((s, i) => i !== i));
    };

    const onHandleCancel = () => {
        setIsShowModal(false);
    };

    const onCancelDeleteAttachment = () => {
        setIsShowAttachmentDeletModal(false);
    };

    const onConfirmDeleteAttachment = () => {
        let formData = new FormData();
        formData.append('documentPath', docPathDeleteFile);
        formData.append('csrftoken', window.csrf_token);
        deleteAttachment(configs.endpoints.DELETE_ATTACHMENT_URL, formData);
        setIsShowAttachmentDeletModal(false);
    };

    const ACCEPTED_FILE_TYPE = ACCEPTED_FILE_TYPES.toString();
    const showErrorModal = () => {
        return (
            <Modal id="file_upload_error">
                <Modal.Header
                    id="unblock_user_modal_title"
                    titleId="ticket.attachment.upload.error.header"
                    onHide={onHandleCancel}
                />
                <Modal.Body>
                    <FormattedMessage id="ticket.attachment.upload.error.body" />
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="cancel"
                        labelId="button.ok"
                        variant="secondary"
                        onClickHandler={onHandleCancel}
                    ></GIButton>
                </Modal.Controls>
            </Modal>
        );
    };

    const showDeleteAttachmentModal = () => {
        return (
            <Modal id="delete_attachment">
                <Modal.Header
                    id="delete_attachment_modal_header"
                    titleId="delete.attachment.modal.title"
                    onHide={onCancelDeleteAttachment}
                />
                <Modal.Body>
                    <FormattedMessage id="delete.attachment.modal.body" />
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="delete_attachment_no"
                        labelId="delete.attachment.modal.button.no"
                        variant="secondary"
                        onClickHandler={onCancelDeleteAttachment}
                        addlClassNames="float-left"
                    ></GIButton>
                    <GIButton
                        id="delete_attachment_yes"
                        labelId="delete.attachment.modal.button.yes"
                        variant="primary"
                        onClickHandler={onConfirmDeleteAttachment}
                    ></GIButton>
                </Modal.Controls>
            </Modal>
        );
    };

    return (
        <Fragment>
            {isShowModal && showErrorModal()}
            {!isShowModal && isShowAttachmentDeletModal && showDeleteAttachmentModal()}
            {(ticket.fileUploadEnabled || attachments.length > 0) && (
                <div className="ticket-file-container">
                    <div className="ticket-header-styled">
                        <h3 className="heading">
                            <FormattedMessage id="ticket.attachments.header" />
                        </h3>
                    </div>
                    <UploadedFileList
                        attachments={attachments}
                        openDeleteAttachmentPopup={onOpenDeleteAttachmentPopup}
                    />
                    <NewFileList files={files} deleteFile={onDeleteFile} />
                    {ticket.fileUploadEnabled && ticket.status !== 'Canceled' && ticket.status !== 'Resolved' && (
                        <div className="attachment-btn">
                            <GIButton
                                id="btn_add_attachment"
                                labelId="ticket.attachments.button.attach-file"
                                onClickHandler={onFileSelect}
                            />
                            <p className={isFileError ? 'create-ticket error--msg' : ''}>
                                <FormattedMessage
                                    id="ticket_file_container.accepted_files_types"
                                    values={{ ACCEPTED_FILE_TYPE }}
                                />
                            </p>
                        </div>
                    )}
                    {files.length > 0 && (
                        <div className="attachment-submit-btn-group">
                            <GIButton
                                id="btn_submit_attachment"
                                labelId="ticket.attachments.button.submit"
                                onClickHandler={onFileUpload}
                            />
                            <GIButton
                                id="btn_cancel_attachment"
                                labelId="ticket.attachments.button.cancel"
                                onClickHandler={onCancelUpload}
                            />
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
};

const mapStateToProps = (state: any) => ({
    ticketFileData: state.individualPortal.ticketFile,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(
        {
            getAttachments,
            postAttachments,
            deleteAttachment,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketFileContainer);
